import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import OrderReviewForm, { ORDER_REVIEW_TEXT_DEFAULTS } from "./OrderReviewForm";
import OrderThankYouPage, { ORDER_THANK_YOU_DEFAULTS } from "./OrderThankYouPage";

const SAMPLE_ORDER = {
  id: "preview",
  orderId: "4013717778",
  buyerName: "Selin Celik",
  shipByDate: "2026-04-16",
  address: {
    raw: "Selin Celik\nKrügerstr. 13\n67065 Ludwigshafen\nDeutschland",
  },
  items: [
    {
      // Beispiel altes Single-Field (Fallback für Bestandsorders)
      title: "Taufkerze mit Trockenblumen Maya",
      variant: "Blumenkranz, Modern",
      quantity: 1,
      personalization:
        "Thessa 09.05.2026\nDenn er hat seinen Engeln befohlen,\n" +
        "dass sie dich behüten auf allen deinen Wegen. Psalm 91,11",
    },
    {
      // Beispiel neues Multi-Field (Etsy Multi-Personalization, seit Mai 2026)
      title: "Stammbuch \"Julia & Niklas\"",
      variant: "Format A5 | Farbe Beige",
      quantity: 1,
      personalizations: [
        { label: "Namen des Paares", value: "Maren & Felix", type: "text" },
        { label: "Familienname",     value: "Kluck",         type: "text" },
        { label: "Hochzeitsdatum",   value: "15.10.2026",    type: "text" },
      ],
      // Fallback-String — würde von pollEtsyNewOrders mitgeschrieben
      personalization:
        "Namen des Paares: Maren & Felix\n" +
        "Familienname: Kluck\n" +
        "Hochzeitsdatum: 15.10.2026",
    },
  ],
};

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle = {
  display: "block", fontWeight: "600", fontSize: "0.8rem",
  color: "#4a5568", marginBottom: "0.3em",
  textTransform: "uppercase", letterSpacing: "0.04em",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "0.55em 0.75em", border: "1px solid #cbd5e0",
  borderRadius: "6px", fontSize: "0.875rem",
  color: "#2d3748", background: "#fff",
  fontFamily: "inherit", marginBottom: "1.25em",
};
const taStyle = { ...inputStyle, resize: "vertical" };

// ── Component ─────────────────────────────────────────────────────────────────

const OrderReviewPreview = () => {
  const [subTab,       setSubTab]       = useState("review");
  const [editMode,     setEditMode]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [reviewForm,   setReviewForm]   = useState(null);
  const [thankYouForm, setThankYouForm] = useState(null);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, "config", "orderReviewPage")),
      getDoc(doc(db, "config", "orderThankYouPage")),
    ]).then(([rSnap, tySnap]) => {
      setReviewForm(rSnap.exists()
        ? { ...ORDER_REVIEW_TEXT_DEFAULTS, ...rSnap.data() }
        : { ...ORDER_REVIEW_TEXT_DEFAULTS });
      setThankYouForm(tySnap.exists()
        ? { ...ORDER_THANK_YOU_DEFAULTS, ...tySnap.data() }
        : { ...ORDER_THANK_YOU_DEFAULTS });
    }).catch(() => {
      setReviewForm({ ...ORDER_REVIEW_TEXT_DEFAULTS });
      setThankYouForm({ ...ORDER_THANK_YOU_DEFAULTS });
    });
  }, []);

  const setReviewField   = (k, v) => setReviewForm((f) => ({ ...f, [k]: v }));
  const setThankYouField = (k, v) => setThankYouForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editMode === "review") {
        await setDoc(doc(db, "config", "orderReviewPage"), reviewForm);
      } else {
        await setDoc(doc(db, "config", "orderThankYouPage"), thankYouForm);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditMode(null);
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubTab = (tab) => { setSubTab(tab); setEditMode(null); };

  if (!reviewForm || !thankYouForm) {
    return (
      <p style={{ color: "#718096", fontStyle: "italic", padding: "2em" }}>
        Lade Konfiguration…
      </p>
    );
  }

  const subTabStyle = (key) => ({
    padding: "0.4em 1.1em", borderRadius: "6px",
    border: `1px solid ${subTab === key ? "#4a2c2a" : "#cbd5e0"}`,
    background: subTab === key ? "#4a2c2a" : "#fff",
    color: subTab === key ? "#fff" : "#4a5568",
    fontWeight: "600", fontSize: "0.85rem", cursor: "pointer",
  });

  const SubTabs = () => (
    <div style={{ display: "flex", gap: "0.5em", marginBottom: "0.85em" }}>
      <button style={subTabStyle("review")} onClick={() => handleSubTab("review")}>
        Bestellformular
      </button>
      <button style={subTabStyle("thankyou")} onClick={() => handleSubTab("thankyou")}>
        Danke-Seite
      </button>
    </div>
  );

  const EditBanner = () => (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: "#ebf8ff", border: "1px solid #90cdf4",
      borderRadius: "8px", padding: "0.75em 1.25em",
      marginBottom: "1.5em", gap: "1em",
    }}>
      <span style={{ color: "#2c5282", fontSize: "0.875rem", fontWeight: "500" }}>
        ✏️ Bearbeitungsmodus — Änderungen wirken sich auf die Live-Seite aus.
      </span>
      <button onClick={() => setEditMode(null)} style={{
        padding: "0.35em 0.9em", borderRadius: "6px",
        border: "1px solid #90cdf4", background: "#fff",
        color: "#2c5282", fontSize: "0.8rem", fontWeight: "600",
        cursor: "pointer", whiteSpace: "nowrap",
      }}>
        ← Vorschau
      </button>
    </div>
  );

  const SaveButton = () => (
    <button onClick={handleSave} disabled={saving} style={{
      padding: "0.65em 2em", borderRadius: "7px", border: "none",
      background: saving ? "#a0aec0" : "#4a2c2a",
      color: "#fff", fontWeight: "700", fontSize: "0.95rem",
      cursor: saving ? "not-allowed" : "pointer",
    }}>
      {saving ? "Wird gespeichert…" : "Änderungen speichern"}
    </button>
  );

  // ── Review form edit mode ──────────────────────────────────────────────────
  if (editMode === "review") {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SubTabs />
        <EditBanner />
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Bestellformular-Text bearbeiten
          </h3>

          <label style={labelStyle}>Seitentitel</label>
          <input style={inputStyle} value={reviewForm.pageTitle}
            onChange={(e) => setReviewField("pageTitle", e.target.value)} />

          <label style={labelStyle}>Bestätigungs-Text (Haupttext)</label>
          <textarea style={{ ...taStyle, minHeight: "90px" }}
            value={reviewForm.confirmMainText}
            onChange={(e) => setReviewField("confirmMainText", e.target.value)} />

          <label style={labelStyle}>Bestätigungs-Hinweise (ein Punkt pro Zeile)</label>
          <textarea style={{ ...taStyle, minHeight: "100px" }}
            value={(reviewForm.confirmBullets || []).join("\n")}
            onChange={(e) =>
              setReviewField("confirmBullets", e.target.value.split("\n"))} />

          <label style={labelStyle}>Änderungswunsch — Bezeichnung</label>
          <input style={inputStyle} value={reviewForm.modificationLabel}
            onChange={(e) => setReviewField("modificationLabel", e.target.value)} />

          <label style={labelStyle}>Änderungswunsch — Hinweistext</label>
          <textarea style={{ ...taStyle, minHeight: "80px" }}
            value={reviewForm.modificationSubtext}
            onChange={(e) =>
              setReviewField("modificationSubtext", e.target.value)} />

          <label style={labelStyle}>Textfeld-Bezeichnung</label>
          <input style={inputStyle} value={reviewForm.feedbackLabel}
            onChange={(e) => setReviewField("feedbackLabel", e.target.value)} />

          <label style={labelStyle}>Textfeld-Platzhalter</label>
          <input style={inputStyle} value={reviewForm.feedbackPlaceholder}
            onChange={(e) =>
              setReviewField("feedbackPlaceholder", e.target.value)} />

          <label style={labelStyle}>Beschriftung Senden-Button</label>
          <input style={{ ...inputStyle, marginBottom: "1.75em" }}
            value={reviewForm.submitButtonText}
            onChange={(e) =>
              setReviewField("submitButtonText", e.target.value)} />

          <SaveButton />
        </div>
      </div>
    );
  }

  // ── Thank-you edit mode ────────────────────────────────────────────────────
  if (editMode === "thankyou") {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SubTabs />
        <EditBanner />
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Danke-Seite bearbeiten
          </h3>

          <label style={labelStyle}>Überschrift</label>
          <input style={inputStyle} value={thankYouForm.headline}
            onChange={(e) => setThankYouField("headline", e.target.value)} />

          <label style={labelStyle}>Einleitungstext</label>
          <textarea style={{ ...taStyle, minHeight: "70px" }}
            value={thankYouForm.subtext}
            onChange={(e) => setThankYouField("subtext", e.target.value)} />

          <label style={labelStyle}>Abschnittstitel</label>
          <input style={inputStyle} value={thankYouForm.nextStepsTitle}
            onChange={(e) =>
              setThankYouField("nextStepsTitle", e.target.value)} />

          <label style={labelStyle}>Schritte (ein Punkt pro Zeile)</label>
          <textarea style={{ ...taStyle, minHeight: "120px" }}
            value={(thankYouForm.nextStepsBullets || []).join("\n")}
            onChange={(e) =>
              setThankYouField("nextStepsBullets", e.target.value.split("\n"))} />

          <label style={labelStyle}>Fußnote</label>
          <textarea style={{ ...taStyle, minHeight: "60px", marginBottom: "1.75em" }}
            value={thankYouForm.footerNote}
            onChange={(e) => setThankYouField("footerNote", e.target.value)} />

          <SaveButton />
        </div>
      </div>
    );
  }

  // ── Thank-you preview ──────────────────────────────────────────────────────
  if (subTab === "thankyou") {
    return (
      <div>
        <SubTabs />
        <div style={{
          maxWidth: 560, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "1em", marginBottom: "0.75em",
        }}>
          <div style={{
            flex: 1, background: "#fffbeb", border: "1px solid #f6e05e",
            borderRadius: "8px", padding: "0.6em 1em",
            color: "#744210", fontSize: "0.8rem", fontWeight: "500",
          }}>
            Vorschau-Modus — so sehen deine Kunden die Danke-Seite.
            {saved && (
              <span style={{ marginLeft: "0.75em", color: "#276749",
                fontWeight: "700" }}>
                ✓ Gespeichert!
              </span>
            )}
          </div>
          <button onClick={() => setEditMode("thankyou")} style={{
            padding: "0.45em 1.1em", borderRadius: "6px",
            border: "1px solid #cbd5e0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
            cursor: "pointer", whiteSpace: "nowrap",
          }}>
            ✏️ Text bearbeiten
          </button>
        </div>
        <OrderThankYouPage textConfig={thankYouForm} />
      </div>
    );
  }

  // ── Review form preview ────────────────────────────────────────────────────
  return (
    <div>
      <SubTabs />
      <div style={{
        maxWidth: 600, margin: "0 auto",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "1em", marginBottom: "0.75em",
      }}>
        <div style={{
          flex: 1, background: "#fffbeb", border: "1px solid #f6e05e",
          borderRadius: "8px", padding: "0.6em 1em",
          color: "#744210", fontSize: "0.8rem", fontWeight: "500",
        }}>
          Vorschau-Modus — so sehen deine Kunden das Bestellformular.
          {saved && (
            <span style={{ marginLeft: "0.75em", color: "#276749",
              fontWeight: "700" }}>
              ✓ Gespeichert!
            </span>
          )}
        </div>
        <button onClick={() => setEditMode("review")} style={{
          padding: "0.45em 1.1em", borderRadius: "6px",
          border: "1px solid #cbd5e0", background: "#fff",
          color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
          cursor: "pointer", whiteSpace: "nowrap",
        }}>
          ✏️ Text bearbeiten
        </button>
      </div>
      <OrderReviewForm
        previewMode
        sampleData={{ order: SAMPLE_ORDER }}
        textConfig={reviewForm}
      />
    </div>
  );
};

export default OrderReviewPreview;
